import { gql } from "@apollo/client";

// Account/User related fragments
export const ACCOUNT_BASIC_INFO = gql`
  fragment AccountBasicInfo on Account {
    id
    firstName
    lastName
    deletedAt
    timeZone
    summary
    leadType
    Id_Ehr
    contactPhoneNumber
    activityPhoneNumber
    bdlProgramStartOn
    bdlProgramEndOn
    latest_program_type
    latest_program_type_status
    programStartOn
    programEndOn
  }
`;

export const USER_INFO = gql`
  fragment UserInfo on User {
    id
    firstName
    lastName
    email
    isTestUser
  }
`;

export const SPECIALIST_INFO = gql`
  fragment SpecialistInfo on User {
    id
    firstName
    lastName
    email
  }
`;

// Playlist related fragments
export const PLAYLIST_BASIC_INFO = gql`
  fragment PlaylistBasicInfo on Playlist {
    id
    title
    startOn
    endOn
    isActive
  }
`;

export const PLAYLIST_WITH_PROGRAM = gql`
  fragment PlaylistWithProgram on Playlist {
    ...PlaylistBasicInfo
    program {
      id
    }
  }
  ${PLAYLIST_BASIC_INFO}
`;

// Voice message related fragments
export const VOICE_MESSAGE_INFO = gql`
  fragment VoiceMessageInfo on VoiceMessage {
    title
    templateLink
    feature
    cognitiveDomains
  }
`;

export const VOICE_MESSAGE_DETAILED = gql`
  fragment VoiceMessageDetailed on VoiceMessage {
    id
    title
    feature
    templateLink
    focus
    cognitiveDomains
    audioFile {
      id
      durationInSeconds
    }
  }
`;

export const PLAYLIST_VOICE_MESSAGE = gql`
  fragment PlaylistVoiceMessage on PlaylistVoiceMessage {
    voiceMessage {
      ...VoiceMessageInfo
    }
  }
  ${VOICE_MESSAGE_INFO}
`;

export const PLAYLIST_VOICE_MESSAGE_DETAILED = gql`
  fragment PlaylistVoiceMessageDetailed on PlaylistVoiceMessage {
    id
    order
    createdOn
    updatedOn
    playlist {
      id
      title
    }
    voiceMessage {
      ...VoiceMessageDetailed
    }
  }
  ${VOICE_MESSAGE_DETAILED}
`;

export const PLAYLIST_DETAILED = gql`
  fragment PlaylistDetailed on Playlist {
    id
    title
    isActive
    startOn
    endOn
    createdOn
    updatedOn
    deletedAt
    program {
      id
      round
      program_type
      start_date
      end_date
    }
    playlistVoiceMessages {
      ...PlaylistVoiceMessageDetailed
    }
  }
  ${PLAYLIST_VOICE_MESSAGE_DETAILED}
`;

// Call record related fragments
export const CALL_RECORD_BASIC = gql`
  fragment CallRecordBasic on CallRecord {
    sid
    durationInSeconds
    outcome
    type
    createdOn
    recordingDownloadUrl
    programRating
    fullProgramRatingResponse
    reflectionOnLearnings
    errorDetails
    hangupDetails
    hbacalldata
    sessionCount
  }
`;

export const CALL_RECORD_WITH_PLAYLIST = gql`
  fragment CallRecordWithPlaylist on CallRecord {
    ...CallRecordBasic
    playlist {
      ...PlaylistBasicInfo
    }
  }
  ${CALL_RECORD_BASIC}
  ${PLAYLIST_BASIC_INFO}
`;

// Complete account info with all relations
export const ACCOUNT_FULL_INFO = gql`
  fragment AccountFullInfo on Account {
    ...AccountBasicInfo
    specialist {
      ...SpecialistInfo
    }
    user {
      ...UserInfo
    }
    playlists {
      ...PlaylistWithProgram
    }
    latestProgram {
        id
        program_type
        start_date
        end_date
    }
  }
  ${ACCOUNT_BASIC_INFO}
  ${SPECIALIST_INFO}
  ${USER_INFO}
  ${PLAYLIST_WITH_PROGRAM}
`;

// Treatment/Program related fragments
export const PROGRAM_HISTORY_INFO = gql`
  fragment ProgramHistoryInfo on ProgramHistory {
    id
    program_type
    start_date
    end_date
    round
    created_on
    updated_at
    status
  }
`;

export const PROGRAM_HISTORY_WITH_SPECIALIST = gql`
  fragment ProgramHistoryWithSpecialist on ProgramHistory {
    ...ProgramHistoryInfo
    specialist {
      ...SpecialistInfo
    }
  }
  ${PROGRAM_HISTORY_INFO}
  ${SPECIALIST_INFO}
`;

export const TREATMENT_INFO = gql`
  fragment TreatmentInfo on Treatment {
    id
    program_type
    start_date
    end_date
    specialist {
      ...SpecialistInfo
    }
  }
  ${SPECIALIST_INFO}
`;

// Call schedule related fragments
export const CALL_SCHEDULE_INFO = gql`
  fragment CallScheduleInfo on CallSchedule {
    id
    cronExpression
    scheduleDescription
    callFlowVersion
    createdOn
  }
`;

// Get single user specific fragments
export const GET_SINGLE_USER_ACCOUNT = gql`
  fragment GetSingleUserAccount on Account {
    id
    firstName
    lastName
    contactPhoneNumber
    activityPhoneNumber
    timeZone
    summary
    programStartOn
    programEndOn
    bdlProgramStartOn
    bdlProgramEndOn
    createdOn
    deletedAt
    leadType
    Id_Ehr
    latest_program_type
    user {
      ...UserInfo
    }
    specialist {
      id
      firstName
    }
    playlists {
      ...PlaylistDetailed
    }
    callRecords {
      sid
      activities {
        id
        callSid
        voiceMessage {
          id
        }
      }
    }
    callSchedules {
      ...CallScheduleInfo
    }
  }
  ${USER_INFO}
  ${PLAYLIST_DETAILED}
  ${CALL_SCHEDULE_INFO}
`;
